import type { OrderCustomFormInIntervention, Priority } from 'api/models/mainservice';
import type { GeoCoordinate, TableQuery } from 'common/models';
import type { Address } from 'pages/customers/models';

export type InterventionState =
  | 'Planned'
  | 'Running'
  | 'WaitingToSignature'
  | 'WaitingToConfirm'
  | 'Completed'
  | 'Cancelled'
  | 'DepartureStarted';

export const InterventionStateArray = [
  'Planned',
  'DepartureStarted',
  'Running',
  'WaitingToSignature',
  'WaitingToConfirm',
  'Completed',
  'Cancelled'
];

export type InterventionNotificationType = 'Planned' | 'Reminder' | 'Cancel';

export const InterventionNotificationArray = ['Planned', 'Reminder']; // We are not supporting cancel type for send manually

export type InterventionDepartureNotificationType = 'DepartureStarted' | 'DepartureLate';

export const InterventionDepartureNotificationArray = ['DepartureStarted', 'DepartureLate'];

// TODO: update interface after intervention and order split
export interface RelatedDataIntervention {
  primaryAssigneeId: string;
  // orderId: string;
  deviceId?: string | null;
  day: string;
  startedAt?: string | null;
  endedAt?: string | null;
  confirmedAt?: string | null;
  cancelledAt?: string | null;
  signaturesConfirmedAt?: string | null;
  // sendReminderOneDayBefore: boolean;
  cancelReason?: string | null;
  cancelledById?: string | null;
  confirmedById?: string | null;
  assignations: RelatedDataInterventionAssignation[];
  state: InterventionState;
  id: string;
  created: string;
  modified: string;
}

export interface RelatedDataInterventionAssignation extends InterventionAssignation {
  fullName?: string | null;
}

export interface InterventionAssignation {
  userId: string;
  plannedStart: string;
  plannedEnd: string;
}

export interface InterventionAssignationToAdd {
  userId: string;
  plannedStart: string;
  plannedEnd: string;
}

export interface InterventionAssignationToEdit {
  userId: string;
  plannedStart: string;
  plannedEnd: string;
}

export interface InterventionDetail {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  externalId?: string | null;
  /** @format uuid */
  categoryId: string;
  categoryName: string;
  /** @format uuid */
  customStateId?: string | null;
  customStateName?: string | null;
  /** @format uuid */
  ownerId: string;
  ownerName: string;
  /** @format uuid */
  primaryAssigneeId: string;
  /** @format uuid */
  orderId: string;
  /** @format uuid */
  deviceId?: string | null;
  /** @format date-time */
  day: string;
  /** @format date-time */
  departureStartedAt?: string | null;
  /** @format date-time */
  startedAt?: string | null;
  /** @format date-time */
  endedAt?: string | null;
  /** @format date-time */
  confirmedAt?: string | null;
  /** @format date-time */
  cancelledAt?: string | null;
  /** @format date-time */
  signaturesConfirmedAt?: string | null;
  cancelReason?: string | null;
  /** @format uuid */
  cancelledById?: string | null;
  /** @format uuid */
  confirmedById?: string | null;
  sendReminderOneDayBefore: boolean;
  assignations: InterventionAssignation[];
  startedLocation?: GeoCoordinate | null;
  endLocation?: GeoCoordinate | null;
  signaturesConfirmedLocation?: GeoCoordinate | null;
  state: InterventionState;
  internalNote?: string | null;
  /** @format double */
  totalPrice?: number | null;
  /** @format double */
  travelPrice?: number | null;
  /** @format int32 */
  travelDistance?: number | null;
  /** @format double */
  workPrice?: number | null;
  /** @format double */
  workHours?: number | null;
  /** @format double */
  materialPrice?: number | null;
  /** @format double */
  materialCount?: number | null;
}

export interface InterventionFormObject {
  primaryAssigneeId: string;
  day: string;
  assignations: InterventionAssignation[];
  sendReminderOneDayBefore: boolean;
  eventStartTime?: string;
  eventEndTime?: string;
  internalNote?: string | null;
  categoryId?: string | null;
  customStateId?: string | null;
  orderId?: string | null;
  address?: Address;
  customerId?: string | null;
  priority: Priority;
  externalId?: string | null;
}

export interface InterventionFormObjectWithId extends InterventionFormObject {
  id: string;
}

export interface InterventionToEdit {
  /** @format uuid */
  customStateId?: string | null;
  /** @format uuid */
  ownerId?: string;
  internalNote?: string | null;
  sendByPost?: boolean;
}

export const INTERVENTION_SORT_KEYS = [
  'created',
  'day',
  'code',
  'orderName',
  'customerName',
  'address',
  'categoryName',
  'orderCategoryName',
  'primaryAssignee',
  'internalNote',
  'customStateName',
  'ownerName',
  'priority'
] as const;

export type InterventionSortBy = (typeof INTERVENTION_SORT_KEYS)[number];

export interface InterventionTableQuery extends TableQuery<InterventionSortBy> {
  state?: string | null;
  categoryId?: string | null;
  orderState?: string | null;
  orderCategoryId?: string | null;
  orderOwnerId?: string | null;
  primaryAssigneeId?: string | null;
  assigneeIds?: string | null;
  searchPhrase?: string | null;
  customerId?: string | null;
  customStateId?: string | null;
  ownerId?: string | null;
}

export interface InterventionInOrder {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  externalId?: string | null;
  /** @format uuid */
  categoryId: string;
  categoryName: string;
  /** @format uuid */
  customStateId?: string | null;
  customStateName?: string | null;
  /** @format uuid */
  ownerId: string;
  ownerName: string;
  /** @format uuid */
  primaryAssigneeId: string;
  primaryAssigneeName: string;
  /** @format uuid */
  orderId: string;
  /** @format uuid */
  deviceId?: string | null;
  /** @format date-time */
  day: string;
  /** @format date-time */
  departureStartedAt?: string | null;
  /** @format date-time */
  startedAt?: string | null;
  /** @format date-time */
  endedAt?: string | null;
  /** @format date-time */
  confirmedAt?: string | null;
  /** @format date-time */
  cancelledAt?: string | null;
  /** @format date-time */
  signaturesConfirmedAt?: string | null;
  sendReminderOneDayBefore: boolean;
  cancelReason?: string | null;
  /** @format uuid */
  cancelledById?: string | null;
  /** @format uuid */
  confirmedById?: string | null;
  assignations: InterventionAssignation[];
  state: InterventionState;
  internalNote?: string | null;
  /** @format double */
  totalPrice?: number | null;
  /** @format double */
  travelPrice?: number | null;
  /** @format int32 */
  travelDistance?: number | null;
  /** @format double */
  workPrice?: number | null;
  /** @format double */
  workHours?: number | null;
  /** @format double */
  materialPrice?: number | null;
  /** @format double */
  materialCount?: number | null;
}

export interface InterventionDetailAlt {
  externalId?: string | null;
  /** @format uuid */
  categoryId: string;
  categoryName: string;
  /** @format uuid */
  customStateId?: string | null;
  customStateName?: string | null;
  /** @format uuid */
  ownerId: string;
  ownerName: string;
  /** @format uuid */
  primaryAssigneeId: string;
  primaryAssigneeName: string;
  priority: Priority;
  /** @format uuid */
  orderId: string;
  /** @format uuid */
  deviceId?: string | null;
  /** @format date */
  day: string;
  /** @format date-time */
  departureStartedAt?: string | null;
  /** @format date-time */
  startedAt?: string | null;
  /** @format date-time */
  endedAt?: string | null;
  /** @format date-time */
  confirmedAt?: string | null;
  /** @format date-time */
  cancelledAt?: string | null;
  /** @format date-time */
  signaturesConfirmedAt?: string | null;
  cancelReason?: string | null;
  /** @format uuid */
  cancelledById?: string | null;
  /** @format uuid */
  confirmedById?: string | null;
  sendReminderOneDayBefore: boolean;
  assignations: InterventionAssignation[];
  customForms: OrderCustomFormInIntervention[];
  departureStartedLocation?: GeoCoordinate | null;
  startedLocation?: GeoCoordinate | null;
  endLocation?: GeoCoordinate | null;
  signaturesConfirmedLocation?: GeoCoordinate | null;
  state: InterventionState;
  internalNote?: string | null;
  /** @format double */
  totalPrice?: number | null;
  /** @format double */
  travelPrice?: number | null;
  /** @format int32 */
  travelDistance?: number | null;
  /** @format double */
  workPrice?: number | null;
  /** @format double */
  workHours?: number | null;
  /** @format double */
  materialPrice?: number | null;
  /** @format double */
  materialCostPrice?: number | null;
  /** @format double */
  materialCount?: number | null;
  sendByPost: boolean;
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
}
